import React, { useContext, useEffect, useState } from 'react'
import { TrendingUp, TrendingDown, Users, Target, AlertCircle, CheckCircle, XCircle, DollarSign, BarChart3, Filter, PieChart, Activity, Award } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, Area, AreaChart, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './LeadsAnalytics.module.css'

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

const LeadsAnalytics = () => {
    const authCtx = useContext(AuthContext)

    const [isGettingLeadSources, setIsGettingLeadSources] = useState(false)
    const [leadSources, setLeadSources] = useState([])

    const getAllStylistLeadSources = async () => {
        setIsGettingLeadSources(true)
        const getAllStylistLeadSourcesResponse = await fetch(BASE_URL_API + "/getAllStylistLeadSources",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    userIdentifier: authCtx.userIdentifier
                })
            });

        if (!getAllStylistLeadSourcesResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getAllStylistLeadSourcesRespo = await getAllStylistLeadSourcesResponse.json()
            if (getAllStylistLeadSourcesRespo.status === "success") {
                setLeadSources(getAllStylistLeadSourcesRespo.response)
            } else {
                if (getAllStylistLeadSourcesRespo.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {
                    setLeadSources([])
                }
            }
            console.log(getAllStylistLeadSourcesRespo)
        }
        setIsGettingLeadSources(false)
    }

    useEffect(() => {
        getAllStylistLeadSources()
    }, [])

    const getRate = (nr, dr) => {
        if (!dr || dr === 0) return 0
        return parseFloat(parseFloat(parseFloat(nr) / parseFloat(dr)) * 100).toFixed(2)
    }

    const formatLeadSource = (source) => {
        if (!source) return 'Unidentified Sources'
        return source.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ')
    }

    const getTotalStats = () => {
        const totals = leadSources.reduce((acc, item) => ({
            totalLeads: acc.totalLeads + parseInt(item.totalLeads || 0),
            totalAssigned: acc.totalAssigned + parseInt(item.totalAssigned || 0),
            convertedLeads: acc.convertedLeads + parseInt(item.convertedLeads || 0),
            totalInvalid: acc.totalInvalid + parseInt(item.totalInvalid || 0)
        }), { totalLeads: 0, totalAssigned: 0, convertedLeads: 0, totalInvalid: 0 })

        return totals
    }

    // Prepare chart data
    const getChartData = () => {
        return leadSources.map(item => ({
            name: formatLeadSource(item.leadSource),
            totalLeads: parseInt(item.totalLeads),
            converted: parseInt(item.convertedLeads),
            invalid: parseInt(item.totalInvalid),
            assigned: parseInt(item.totalAssigned),
            conversionRate: parseFloat(getRate(item.convertedLeads, item.totalAssigned))
        })).sort((a, b) => b.totalLeads - a.totalLeads)
    }

    const getPieChartData = () => {
        return leadSources.map(item => ({
            name: formatLeadSource(item.leadSource),
            value: parseInt(item.totalLeads)
        })).sort((a, b) => b.value - a.value)
    }

    const getConversionData = () => {
        return leadSources.map(item => ({
            name: formatLeadSource(item.leadSource),
            rate: parseFloat(getRate(item.convertedLeads, item.totalAssigned))
        })).sort((a, b) => b.rate - a.rate)
    }

    const getPerformanceData = () => {
        return leadSources.map(item => {
            const convRate = parseFloat(getRate(item.convertedLeads, item.totalAssigned))
            const invalidRate = parseFloat(getRate(item.totalInvalid, item.totalAssigned))
            const efficiency = 100 - invalidRate

            return {
                subject: formatLeadSource(item.leadSource).substring(0, 15),
                conversion: convRate,
                efficiency: efficiency,
                volume: (parseInt(item.totalLeads) / 1000) * 10
            }
        }).slice(0, 6)
    }

    if (authCtx.userIdentifier !== "999964N8406") {
        return (
            <div className={styles.unauthorizedWrapper}>
                <div className={styles.unauthorizedCard}>
                    <div className={styles.unauthorizedIcon}>
                        <AlertCircle size={32} />
                    </div>
                    <h2 className={styles.unauthorizedTitle}>Access Denied</h2>
                    <p className={styles.unauthorizedText}>You are not authorized to view this dashboard.</p>
                </div>
            </div>
        )
    }

    const totals = getTotalStats()
    const conversionRate = getRate(totals.convertedLeads, totals.totalAssigned)
    const invalidRate = getRate(totals.totalInvalid, totals.totalAssigned)
    const chartData = getChartData()
    const pieData = getPieChartData()
    const conversionData = getConversionData()
    const performanceData = getPerformanceData()

    // Custom tooltip for charts
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.chartTooltip}>
                    <p className={styles.tooltipLabel}>{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}: {entry.value.toLocaleString()}
                        </p>
                    ))}
                </div>
            )
        }
        return null
    }

    return (
        <div className={styles.mainWrapper}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerContent}>
                        <div>
                            <h1 className={styles.headerTitle}>
                                <div className={styles.headerIcon}>
                                    <BarChart3 size={24} />
                                </div>
                                Leads Analytics Dashboard
                            </h1>
                            <p className={styles.headerSubtitle}>Advanced insights and performance metrics across all lead sources</p>
                        </div>
                        <button
                            onClick={getAllStylistLeadSources}
                            className={styles.refreshButton}
                        >
                            Refresh Data
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.contentContainer}>
                {isGettingLeadSources ? (
                    <div className={styles.loadingWrapper}>
                        <div className={styles.spinner}></div>
                        <p className={styles.loadingTitle}>Collecting leads data...</p>
                        <p className={styles.loadingSubtitle}>This will just take a moment</p>
                    </div>
                ) : (
                    <>
                        {/* Stats Cards */}
                        <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                                <div className={styles.statCardHeader}>
                                    <div className={`${styles.statIcon} ${styles.statIconBlue}`}>
                                        <Users size={24} />
                                    </div>
                                    <TrendingUp size={20} className={styles.trendIconGreen} />
                                </div>
                                <p className={styles.statLabel}>Total Leads</p>
                                <p className={styles.statValue}>{totals.totalLeads.toLocaleString()}</p>
                                <p className={styles.statChange}>Across {leadSources.length} sources</p>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statCardHeader}>
                                    <div className={`${styles.statIcon} ${styles.statIconGreen}`}>
                                        <CheckCircle size={24} />
                                    </div>
                                    <span className={styles.statBadgeGreen}>{conversionRate}%</span>
                                </div>
                                <p className={styles.statLabel}>Converted</p>
                                <p className={styles.statValue}>{totals.convertedLeads.toLocaleString()}</p>
                                <p className={styles.statChange}>Conversion Rate</p>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statCardHeader}>
                                    <div className={`${styles.statIcon} ${styles.statIconRed}`}>
                                        <XCircle size={24} />
                                    </div>
                                    <span className={styles.statBadgeRed}>{invalidRate}%</span>
                                </div>
                                <p className={styles.statLabel}>Invalid Leads</p>
                                <p className={styles.statValue}>{totals.totalInvalid.toLocaleString()}</p>
                                <p className={styles.statChange}>Quality Improvement Needed</p>
                            </div>

                            <div className={styles.statCard}>
                                <div className={styles.statCardHeader}>
                                    <div className={`${styles.statIcon} ${styles.statIconPurple}`}>
                                        <Target size={24} />
                                    </div>
                                    <Award size={20} className={styles.trendIconOrange} />
                                </div>
                                <p className={styles.statLabel}>Assigned</p>
                                <p className={styles.statValue}>{totals.totalAssigned.toLocaleString()}</p>
                                <p className={styles.statChange}>Ready for Processing</p>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className={styles.chartsGrid}>
                            {/* Lead Volume by Source - Bar Chart */}
                            <div className={styles.chartCard}>
                                <div className={styles.chartHeader}>
                                    <div>
                                        <h3 className={styles.chartTitle}>Lead Volume by Source</h3>
                                        <p className={styles.chartSubtitle}>Total, Converted & Invalid leads comparison</p>
                                    </div>
                                    <BarChart3 size={20} className={styles.chartIcon} />
                                </div>
                                <div className={styles.chartContainer}>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis
                                                dataKey="name"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                            <Bar dataKey="totalLeads" fill="#2563eb" name="Total Leads" radius={[8, 8, 0, 0]} />
                                            <Bar dataKey="converted" fill="#10b981" name="Converted" radius={[8, 8, 0, 0]} />
                                            <Bar dataKey="invalid" fill="#ef4444" name="Invalid" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Lead Distribution - Pie Chart */}
                            <div className={styles.chartCard}>
                                <div className={styles.chartHeader}>
                                    <div>
                                        <h3 className={styles.chartTitle}>Lead Distribution</h3>
                                        <p className={styles.chartSubtitle}>Market share by source</p>
                                    </div>
                                    <PieChart size={20} className={styles.chartIcon} />
                                </div>
                                <div className={styles.chartContainer}>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <RePieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={100}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </RePieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Conversion Rate Trend - Area Chart */}
                            <div className={styles.chartCard}>
                                <div className={styles.chartHeader}>
                                    <div>
                                        <h3 className={styles.chartTitle}>Conversion Rate Analysis</h3>
                                        <p className={styles.chartSubtitle}>Performance by source</p>
                                    </div>
                                    <Activity size={20} className={styles.chartIcon} />
                                </div>
                                <div className={styles.chartContainer}>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <AreaChart data={conversionData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                            <defs>
                                                <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis
                                                dataKey="name"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone"
                                                dataKey="rate"
                                                stroke="#10b981"
                                                fillOpacity={1}
                                                fill="url(#colorRate)"
                                                name="Conversion Rate %"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Performance Radar */}
                            <div className={styles.chartCard}>
                                <div className={styles.chartHeader}>
                                    <div>
                                        <h3 className={styles.chartTitle}>Multi-Dimensional Performance</h3>
                                        <p className={styles.chartSubtitle}>Conversion, efficiency & volume analysis</p>
                                    </div>
                                    <Target size={20} className={styles.chartIcon} />
                                </div>
                                <div className={styles.chartContainer}>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <RadarChart data={performanceData}>
                                            <PolarGrid stroke="#e5e7eb" />
                                            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                                            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11 }} />
                                            <Radar name="Conversion %" dataKey="conversion" stroke="#2563eb" fill="#2563eb" fillOpacity={0.6} />
                                            <Radar name="Efficiency %" dataKey="efficiency" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                                            <Radar name="Volume Score" dataKey="volume" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
                                            <Legend />
                                            <Tooltip />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Lead Sources Table */}
                        <div className={styles.leadSourcesSection}>
                            <div className={styles.sectionHeader}>
                                <h2 className={styles.sectionTitle}>Detailed Lead Sources Breakdown</h2>
                                <button className={styles.filterButton}>
                                    <Filter size={16} />
                                    Filter
                                </button>
                            </div>

                            <div className={styles.leadSourceContainer}>
                                {leadSources?.map((item, index) => {
                                    const convRate = getRate(item.convertedLeads, item.totalAssigned)
                                    const invalidPercentage = getRate(item.totalInvalid, item.totalAssigned)
                                    const missingLeads = parseFloat(item.totalLeads) - (parseFloat(item.totalInvalid) + parseFloat(item.convertedLeads))
                                    const missingRate = getRate(missingLeads, item.totalLeads)
                                    const costPerLead = (11700 / 11000).toFixed(2)

                                    return (
                                        <div
                                            key={index}
                                            className={styles.eachLeadSource}
                                        >
                                            {/* Header */}
                                            <div className={styles.leadSourceHeader}>
                                                <div>
                                                    <h3 className={styles.leadVolume}>
                                                        {parseInt(item.totalLeads).toLocaleString()}
                                                    </h3>
                                                    <p className={styles.leadSource}>
                                                        {formatLeadSource(item.leadSource)}
                                                    </p>
                                                </div>
                                                <div className={`${styles.conversionBadge} ${convRate >= 30 ? styles.conversionHigh :
                                                        convRate >= 20 ? styles.conversionMedium :
                                                            styles.conversionLow
                                                    }`}>
                                                    {convRate}% Conv.
                                                </div>
                                            </div>

                                            {/* Metrics */}
                                            <div className={styles.metricsContainer}>
                                                <div className={styles.metricRow}>
                                                    <div className={styles.metricLabel}>
                                                        <div className={`${styles.metricDot} ${styles.dotBlue}`}></div>
                                                        <span>Assigned</span>
                                                    </div>
                                                    <div className={styles.metricValue}>
                                                        <span className={styles.metricNumber}>{parseInt(item.totalAssigned).toLocaleString()}</span>
                                                        <span className={styles.metricPercent}>
                                                            ({getRate(item.totalAssigned, item.totalLeads)}%)
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className={styles.metricRow}>
                                                    <div className={styles.metricLabel}>
                                                        <div className={`${styles.metricDot} ${styles.dotGreen}`}></div>
                                                        <span>Converted</span>
                                                    </div>
                                                    <div className={styles.metricValue}>
                                                        <span className={`${styles.metricNumber} ${styles.textGreen}`}>{parseInt(item.convertedLeads).toLocaleString()}</span>
                                                        <span className={styles.metricPercent}>
                                                            ({convRate}%)
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className={styles.metricRow}>
                                                    <div className={styles.metricLabel}>
                                                        <div className={`${styles.metricDot} ${styles.dotRed}`}></div>
                                                        <span>Invalid</span>
                                                    </div>
                                                    <div className={styles.metricValue}>
                                                        <span className={`${styles.metricNumber} ${styles.textRed}`}>{parseInt(item.totalInvalid).toLocaleString()}</span>
                                                        <span className={styles.metricPercent}>
                                                            ({invalidPercentage}%)
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className={styles.metricRow}>
                                                    <div className={styles.metricLabel}>
                                                        <div className={`${styles.metricDot} ${styles.dotOrange}`}></div>
                                                        <span>Missing</span>
                                                    </div>
                                                    <div className={styles.metricValue}>
                                                        <span className={`${styles.metricNumber} ${styles.textOrange}`}>{missingLeads.toLocaleString()}</span>
                                                        <span className={styles.metricPercent}>
                                                            ({missingRate}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Cost Per Lead */}
                                            <div className={styles.costSection}>
                                                <div className={styles.costLabel}>
                                                    <DollarSign size={16} />
                                                    <span>Cost per Lead</span>
                                                </div>
                                                <span className={styles.costValue}>₹{costPerLead}</span>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className={styles.progressBarContainer}>
                                                <div className={styles.progressBar}>
                                                    <div
                                                        className={styles.progressBarFill}
                                                        style={{ width: `${convRate}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default LeadsAnalytics